import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import {
    IconChevronDown,
    IconChevronUp,
    IconPlayerPlayFilled,
    IconVideo,
} from "@tabler/icons-react";

import { Button, Text } from "@fragments";
import api from "@helpers/api";

import Preview from "./Preview";
import PlayerModal from "./PlayerModal";
import { readMinimized, writeMinimized } from "./store";

import "./styles.scss";

// Fast and calm: the Accordion fragment's curve, shorter.
const TRANSITION = { duration: 0.2, ease: "easeInOut" };

const COLLAPSE = {
    initial: { opacity: 0, height: 0 },
    animate: { opacity: 1, height: "auto" },
    exit: { opacity: 0, height: 0 },
};

function formatDuration(seconds) {
    if (!seconds) return null;
    const total = Math.round(seconds);
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const rest = String(total % 60).padStart(2, "0");
    return hours > 0
        ? `${hours}:${String(minutes).padStart(2, "0")}:${rest}`
        : `${minutes}:${rest}`;
}

/**
 * Welcome video card in the community header's description column.
 *
 * Large until the member watches the video, then minimal. Both states
 * minimize/expand by hand, and that choice is kept per device. The large
 * state previews the video muted and looping; the whole card opens the
 * player modal, which starts from zero. Title and text come from the
 * creator (`welcome_video.name` / `.description`). Renders nothing when the
 * community has no welcome video (older payloads included).
 *
 * @param {object} info  the community payload (`welcome_video`, `id`)
 */
export default function WelcomeVideo({ info }) {
    const { t } = useTranslation("community");

    const video = info?.welcome_video;
    const communityId = info?.id;
    const contentId = video?.id;

    const [watched, setWatched] = useState(!!video?.watched);
    // null = no manual choice yet: the card follows `watched`.
    const [choice, setChoice] = useState(() => readMinimized(communityId, contentId));
    const [open, setOpen] = useState(false);

    useEffect(() => {
        setWatched(!!video?.watched);
        setChoice(readMinimized(communityId, contentId));
    }, [communityId, contentId, video?.watched]);

    if (!contentId) return null;

    const minimized = choice ?? watched;
    const duration = formatDuration(video.duration);
    const isPortrait = video.aspect_ratio && video.aspect_ratio < 1;
    // Watching only changes the card's size, never its copy.
    const meta = duration ?? t("welcomeVideo.watch");

    function toggle() {
        const next = !minimized;
        setChoice(next);
        writeMinimized(communityId, contentId, next);
    }

    function openPlayer() {
        setOpen(true);
    }

    function handleKeyDown(event) {
        if (event.target !== event.currentTarget) return;
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        openPlayer();
    }

    async function handleClose() {
        setOpen(false);

        // The player marks the video watched on its own (80 %): re-read the
        // flag so the card can settle into its minimal state.
        try {
            const { data } = await api.get(`content/${contentId}`);
            if (data?.id) setWatched(!!data.watched);
        } catch (error) {
            console.log(error);
        }
    }

    const thumbnail = video.thumbnail ? (
        <img src={video.thumbnail} alt="" />
    ) : (
        <span className="welcome-video__fallback" aria-hidden="true">
            <IconVideo />
        </span>
    );

    const playGlyph = (
        <span className="welcome-video__play" aria-hidden="true">
            <IconPlayerPlayFilled />
        </span>
    );

    return (
        <>
            <motion.div
                layout
                transition={TRANSITION}
                className={`welcome-video${minimized ? " is-minimized" : ""}`}
            >
                {/* The whole surface is the action; the minimize toggle is a
                    sibling, so no button nests inside it. */}
                <div
                    className="welcome-video__surface"
                    role="button"
                    tabIndex={0}
                    aria-label={t("welcomeVideo.play")}
                    onClick={openPlayer}
                    onKeyDown={handleKeyDown}
                >
                    <AnimatePresence initial={false} mode="wait">
                        {minimized ? (
                            <motion.div
                                key="minimal"
                                className="welcome-video__minimal"
                                style={{ overflow: "hidden" }}
                                transition={TRANSITION}
                                {...COLLAPSE}
                            >
                                <div className="welcome-video__row">
                                    <div className={`welcome-video__media welcome-video__media--small${isPortrait ? " is-portrait" : ""}`}>
                                        {thumbnail}
                                        {playGlyph}
                                    </div>

                                    <div className="welcome-video__text">
                                        <Text.label contrast={9} className="welcome-video__name">
                                            {video.name}
                                        </Text.label>
                                        <Text.caption contrast={6}>{meta}</Text.caption>
                                    </div>
                                </div>
                            </motion.div>
                        ) : (
                            <motion.div
                                key="large"
                                className="welcome-video__large"
                                style={{ overflow: "hidden" }}
                                transition={TRANSITION}
                                {...COLLAPSE}
                            >
                                <div className="welcome-video__hero">
                                    <div className={`welcome-video__media welcome-video__media--large${isPortrait ? " is-portrait" : ""}`}>
                                        <Preview src={video.src} poster={video.thumbnail} paused={open} />
                                        {playGlyph}
                                    </div>

                                    <div className="welcome-video__body">
                                        <Text.subheading contrast={6} left={<IconVideo />}>
                                            {t("welcomeVideo.eyebrow")}
                                        </Text.subheading>
                                        <Text.h4 weight={500} contrast={9} className="mt-1 welcome-video__name">
                                            {video.name}
                                        </Text.h4>
                                        {video.description && (
                                            <Text.span contrast={6} className="mt-1 welcome-video__description">
                                                {video.description}
                                            </Text.span>
                                        )}
                                        <Text.caption contrast={5} className="mt-2" left={<IconPlayerPlayFilled />}>
                                            {meta}
                                        </Text.caption>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                <Button
                    className="welcome-video__toggle"
                    variant="text"
                    color="overlay"
                    size="small"
                    icon={minimized ? <IconChevronDown /> : <IconChevronUp />}
                    tooltip={minimized ? t("welcomeVideo.expand") : t("welcomeVideo.minimize")}
                    aria-label={minimized ? t("welcomeVideo.expand") : t("welcomeVideo.minimize")}
                    onClick={toggle}
                    preventLoading
                />
            </motion.div>

            <PlayerModal open={open} contentId={contentId} onClose={handleClose} />
        </>
    );
}
